// src/pages/const/DailyView.jsx
import React from "react";
import { useParams } from "react-router-dom";
import FormViewManager from "../../components/common/form/FormViewManager";
import TableCell from "../../components/common/ui/TableCell";

/* ---------------------------------------------------------
   💡 공사일보 본문을 렌더링하는 컴포넌트 (DailyBody)
   - FormViewManager 내부에서 children 으로 들어가는 부분
---------------------------------------------------------- */
function DailyBody({ meta, data }) {
  return (
    <>
      {/* =====================================
 📌 날씨 정보
===================================== */}
      <section className="mb-4">
        <h2 className="text-body-bold mb-2 ">날씨</h2>

        <table className="w-full text-sm border">

          <tbody >
            {/* Row 1 */}
            <tr >
              <TableCell
                label="날씨"
                value={meta.weather || "-"}
                valueBg="bg-white"
                labelWidth="w-20"
                labelBg="bg-gray-100"
              />
              <TableCell
                label="기온"
                value={`${meta.temp_min ?? "-"}℃ / ${meta.temp_max ?? "-"}℃`}
                valueBg="bg-white"
                labelBg="bg-gray-100"
                labelWidth="w-20"
              />
              <TableCell
                label="평균기온"
                value={`${meta.temp_avg ?? "-"}℃`}
                valueBg="bg-white"
                labelBg="bg-gray-100"
                labelWidth="w-20"
              />
            </tr>

            {/* Row 2 */}
            <tr >
              <TableCell
                label="강수량"
                value={`누계 ${meta.precip_total ?? "-"} mm / 평균 ${meta.precip_avg ?? "-"
                  } mm`}
                valueBg="bg-white"
                labelBg="bg-gray-100"
                labelWidth="w-20"
              />
              <TableCell
                label="적설량"
                value={`누계 ${meta.snow_total ?? "-"} cm / 평균 ${meta.snow_avg ?? "-"
                  } cm`}
                valueBg="bg-white"
                labelWidth="w-20"
                labelBg="bg-gray-100"
              />
              <TableCell
                label="풍속"
                value={`평균 ${meta.wind_speed_avg ?? "-"} m/s / 최대 ${meta.wind_speed_max ?? "-"
                  } m/s`}
                valueBg="bg-white"
                labelWidth="w-20"
                labelBg="bg-gray-100"
              />
            </tr>
          </tbody>
        </table>
      </section>

      {/* =====================================
      📌 금일 / 명일 작업사항 (테이블)
  ====================================== */}
      {/* ===============================
    📌 금일 / 명일 작업사항
================================ */}
      <section className="print-avoid-break">
        <h2 className="text-body-bold mb-2">작업사항</h2>

        {/* 확정 작업사항 */}
        <div className="border border-black mb-4 text-sm">
          {/* 제목 */}
          <div className="px-2 py-1 border-b border-black bg-gray-100 text-xs font-medium print-bg">
            확정 작업사항
          </div>

          {/* 내용 */}
          <div className="px-2 py-2 whitespace-pre-wrap text-body">
            {meta.today_work || "작성된 내용 없음"}
          </div>
        </div>

        {/* 예정 작업사항 */}
        <div className="border border-black mb-4 text-sm">
          {/* 제목 */}
          <div className="px-2 py-1 border-b border-black bg-gray-100 text-xs font-medium print-bg">
            예정 작업사항
          </div>

          {/* 내용 */}
          <div className="px-2 py-2 whitespace-pre-wrap text-body">
            {meta.tomorrow_work || "작성된 내용 없음"}
          </div>
        </div>
      </section>


      {/* =====================================
      📌 자재 현황
  ====================================== */}
      <section className="mt-4 print-avoid-break">
        <h2 className="text-body-bold mb-2">자재 현황</h2>

        <table className="w-full table-fixed text-[13px] border border-black border-collapse">
          <thead className="doc-thead">
            <tr>
              <th className="doc-th">자재명</th>
              <th className="doc-th">규격</th>
              <th className="doc-th">단위</th>
              <th className="doc-th">전일누계</th>
              <th className="doc-th">금일자재</th>
              <th className="doc-th-end">누계자재</th>
            </tr>
          </thead>

          <tbody>
            {data.materials?.length ? (
              data.materials.map((r, i) => {
                const total = (r.prev_total || 0) + (r.qty || 0);
                return (
                  <tr key={i}>
                    <td className="doc-td">{r.name}</td>
                    <td className="doc-td">{r.spec}</td>
                    <td className="doc-td">{r.unit}</td>
                    <td className="doc-td">{r.prev_total || 0}</td>
                    <td className="doc-td">{r.qty || 0}</td>
                    <td className="doc-td">{total}</td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={6} className="doc-td text-center text-gray-400">
                  데이터 없음
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>


      {/* =====================================
      📌 장비 현황
  ====================================== */}
      <section className="mt-4 print-avoid-break">
        <h2 className="text-body-bold mb-2">장비 현황</h2>

        <table className="w-full table-fixed text-[13px] border border-black border-collapse">
          <thead className="doc-thead">
            <tr>
              <th className="doc-th">장비명</th>
              <th className="doc-th">규격</th>
              <th className="doc-th">전일누계</th>
              <th className="doc-th">금일장비</th>
              <th className="doc-th-end">누계장비</th>
            </tr>
          </thead>

          <tbody>
            {data.equipments?.length ? (
              data.equipments.map((r, i) => {
                const total = (r.prev_total || 0) + (r.qty || 0);
                return (
                  <tr key={i}>
                    <td className="doc-td">{r.name}</td>
                    <td className="doc-td">{r.spec || "-"}</td>
                    <td className="doc-td">{r.prev_total || 0}</td>
                    <td className="doc-td">{r.qty || 0}</td>
                    <td className="doc-td">{total}</td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={5} className="doc-td text-center text-gray-400">
                  데이터 없음
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>



      {/* =====================================
      📌 인원 현황
  ====================================== */}
      <section className="mt-4 print-avoid-break">
        <h2 className="text-body-bold mb-2">인원 현황</h2>

        <table className="w-full table-fixed text-[13px] border border-black border-collapse">
          <thead className="doc-thead">
            <tr>
              <th className="doc-th">공종</th>
              <th className="doc-th">구분</th>
              <th className="doc-th">전일누계</th>
              <th className="doc-th">금일 인원</th>
              <th className="doc-th-end">누계 인원</th>
            </tr>
          </thead>

          <tbody>
            {data.personnels?.length ? (
              data.personnels.map((r, i) => {
                const total = (r.prev_total || 0) + (r.qty || 0);
                return (
                  <tr key={i}>
                    <td className="doc-td">{r.work_type}</td>
                    <td className="doc-td">{r.category}</td>
                    <td className="doc-td">{r.prev_total || 0}</td>
                    <td className="doc-td">{r.qty || 0}</td>
                    <td className="doc-td">{total}</td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={5} className="doc-td text-center text-gray-400">
                  데이터 없음
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>



    </>
  );
}

/* ---------------------------------------------------------
   💡 DailyView: FormViewManager 호출 + DailyBody 포함
---------------------------------------------------------- */
export default function DailyView() {
  const { id: project, dns } = useParams();

  return (
    <FormViewManager
      params={{
        project,
        dns,
        module: "const",
        doc_type: "DAILY",
      }}
      doc_name="공사일보"
      app_top={true}
      app_action={true}
    >
      <DailyBody />
    </FormViewManager>
  );
}
